import {Injectable, OnDestroy} from '@angular/core';
import {bufferWhen, debounceTime, filter, map, switchMap} from 'rxjs/operators';
import {race, Subject} from 'rxjs';
import {IdToOrderObj} from '../../../../generated/generated-core.model';
import {MxNotificationService} from '../../../shared/services/mx-notification.service';
import {MxGridCustomizerEvent, MxGridHandleOrder} from './mx-grid-customizer-event';
import {MxCollectionDataService} from './mx-collection-data.service';
import {MxGridCollectionService} from './mx-grid-collection.service';

export interface GridOrderService {

    /** Move selected row or rowIndex up */
    onOrderUp(rowIndex?: number): void;

    /** Move selected row or rowIndex down */
    onOrderDown(rowIndex?: number): void;

    /** Flush order changes */
    flush(): void;
}

@Injectable()
export class MxGridOrderService implements GridOrderService, OnDestroy {

    /** UI change of order event */
    private _gridOrderChange$$: Subject<IdToOrderObj[]> = new Subject<IdToOrderObj[]>();

    /** Flush event */
    private _flush$$: Subject<boolean> = new Subject<boolean>();
    private orderChanged$: Subject<void>;

    /** Input Server URL for http call */
    private changeOrderUrl: string;

    private asyncSave: boolean;

    /** Input handle row ordering */
    private orderObjectHandler: MxGridHandleOrder<any>;

    public wasReorder: boolean = false;

    constructor(private collectionService: MxGridCollectionService,
                private service: MxCollectionDataService,
                private notificationService: MxNotificationService) {
    }

    ngOnDestroy(): void {
        this.flush();
        this._gridOrderChange$$.complete();
    }

    public setUp(event: MxGridCustomizerEvent): void {

        this.changeOrderUrl = event.orderUrl;
        this.orderObjectHandler = event.orderCustomizer;
        this.orderChanged$ = event.orderChanged$;
        this.asyncSave = event.gridProps.asyncSaveOrder;

        let orderChangeDebounce$ = this._gridOrderChange$$.pipe(debounceTime(2000));

        this._gridOrderChange$$
            .pipe(
                filter(() => this.asyncSave),
                // buffer so we will not send server too frequently data (user may move up and down few times)
                bufferWhen(() => race([orderChangeDebounce$, this._flush$$])),
                // filter empty events (in case of flush with no data)
                filter(r => r && r.length > 0),
                // flatten events
                map((r: Array<IdToOrderObj[]>) =>
                    r.reduce((acc, curr) => acc.concat(curr)), []),
                // send only unique by id events
                map((r: IdToOrderObj[]) =>
                    r.reduce((map, curr) => map.set(curr.id, curr), new Map<number | string, IdToOrderObj>())),
                map((r: Map<number | string, IdToOrderObj>) => {
                    let unique = [];
                    r.forEach((v) => unique.push(v));
                    return unique;
                }),
                switchMap((r: IdToOrderObj[]) => this.service.changeOrder(this.changeOrderUrl, r))
            )
            .subscribe(
                () => {
                    this.wasReorder = true;
                    this.notificationService.showSuccess('Elements order was changed');
                    this.collectionService.doReload();
                },
                () => {
                    this.notificationService.showFailure(
                        `Failed to change elements order`)
                        .afterClosed()
                        .subscribe(() => this.collectionService.doReload());
                }
            );
    }

    public flush(): void {
        this._flush$$.next(true);
    }


    private onGridItemOrder(selectedRowIndex: number, direction: number, rows: ReadonlyArray<any>): Array<any> {

        const nextRowIndex = selectedRowIndex + direction;
        const orderedRows = rows.slice().sort((a, b) => this.orderObjectHandler.getOrder(a) - this.orderObjectHandler.getOrder(b));
        const element = orderedRows[selectedRowIndex];
        const nextElement = orderedRows[nextRowIndex];

        const nextOrder = this.orderObjectHandler.getOrder(nextElement);
        const order = this.orderObjectHandler.getOrder(element);

        const cur: IdToOrderObj = {id: this.collectionService.extractId(element), order: nextOrder};
        const prev: IdToOrderObj = {id: this.collectionService.extractId(nextElement), order: order};
        const payload: IdToOrderObj[] = [cur, prev];

        let newRows = this.swapGridRows(rows.slice(0), selectedRowIndex, nextRowIndex);

        // update the object order in UI
        this.orderObjectHandler.setOrder(nextOrder, element);
        this.orderObjectHandler.setOrder(order, nextElement);

        // fire event to update the server
        this.doGridItemOrder(payload);
        if (!this.asyncSave) {
            this.flush();
        }
        return newRows;
    }

    private swapGridRows(rows: Array<any>, indexA: number, indexB: number): Array<any> {
        let aTmp = rows[indexA];

        rows[indexA] = rows[indexB];
        rows[indexB] = aTmp;

        return rows;
    }


    onOrderUp(rowIndex?: number): void {
        this.onOrder(-1, rowIndex);
    }

    onOrderDown(rowIndex?: number): void {
        this.onOrder(1, rowIndex);
    }

    onOrderGroup(direction: 'DOWN' | 'UP', groupIndex: number, gridRows: any[]): void {

        const isAnyGroupCollapsed = gridRows.some(row => row.isGroup && row.reduced);

        if (isAnyGroupCollapsed) {
            let gridRowsReduced = [];
            let skip = false;

            for (let i = 0; i < gridRows.length; i++) {
                if (gridRows[i].isGroup) {
                    skip = gridRows[i].reduced;
                }

                if (!skip) {
                    gridRowsReduced.push(gridRows[i]);
                }

                if (skip && gridRows[i].isGroup) {
                    gridRowsReduced.push(gridRows[i]);
                }
            }

            groupIndex = gridRows.findIndex(row => row.value === gridRowsReduced[groupIndex].value);
        }

        let swapWith = this.getSwapGroup(direction, groupIndex, gridRows);
        if (swapWith === groupIndex) {
            return;
        }
        let firstGroupElem = this.getElementIdsInGroup(groupIndex, gridRows);
        let secondGroupElem = this.getElementIdsInGroup(swapWith, gridRows);
        let rows = this.collectionService.rows;
        let swappedRows = direction === 'UP' ? this.swapRows(rows, firstGroupElem, secondGroupElem)
            : this.swapRows(rows, secondGroupElem, firstGroupElem);
        this.collectionService.setRows(swappedRows);
        this.orderChanged$.next();
    }

    private swapRows( rows: ReadonlyArray<any>,  firstGroupElems: Array<number | string>,
                      secondGroupElems: Array<number | string>): Array<any> {
        let result = [];

        rows.forEach((row, index) => {
            if (firstGroupElems.includes(row)) {
                result[index - secondGroupElems.length] = row;
            } else if (secondGroupElems.includes(row)) {
                result[index + firstGroupElems.length] = row;
            } else {
                result[index] = row;
            }
        });
        return result;
    }

    private getElementIdsInGroup(groupIndex: number, rows: any[]): Array<number> {
        let ids = [];
        for (let i = groupIndex + 1; i < rows.length; i++) {
            if (rows[i].isGroup) {
                break;
            }
            ids.push(rows[i]);
        }
        return ids;
    }

    private getSwapGroup(direction: 'DOWN' | 'UP', groupIndex: number,  rows: any[]): number {
        let nextGroupIndex = groupIndex;
        if (direction === 'UP') {
            if (groupIndex === 0) {
                return 0;
            }
            for (let i = groupIndex - 1; i >= 0; i--) {
                if (rows[i].isGroup) {
                    nextGroupIndex = i;
                    break;
                }
            }
        } else {
            for (let i = groupIndex + 1; i < rows.length; i++) {
                if (rows[i].isGroup) {
                    nextGroupIndex = i;
                    break;
                }
            }
        }
        return nextGroupIndex;
    }

    private onOrder(direction: number, rowMoveIndex: number): void {
        let rowIndex = (rowMoveIndex !== undefined) ? rowMoveIndex : this.collectionService.selectedIndex;
        let rows = this.collectionService.rows;

        let orderRows = this.onGridItemOrder(rowIndex, direction, rows);
        this.collectionService.setRows(orderRows);
        this.orderChanged$.next();
    }

    private doGridItemOrder(idsToOrders: IdToOrderObj[]): void {
        if (idsToOrders && idsToOrders.length > 0) {
            this._gridOrderChange$$.next(idsToOrders);
        }
    }
}

